import { z } from 'zod';

/**
 * Documents des sous-traitants (CDC §14) : KBIS, assurance, attestation de manipulation des
 * fluides et autres types ajoutables ; dates d'expiration facultatives / obligatoires selon le
 * type ; le sous-traitant met à jour ses propres documents, l'administrateur contrôle.
 *
 * Choix de l'entreprise : un document manquant ou périmé ne BLOQUE AUCUNE fonctionnalité ; il
 * déclenche des alertes (sous-traitant, responsables, planificateur à l'attribution).
 */
export const DOC_EXPIRY_MODES = ['none', 'optional', 'required'] as const;
export type DocExpiryMode = (typeof DOC_EXPIRY_MODES)[number];
export const DOC_EXPIRY_LABELS: Record<DocExpiryMode, string> = { none: 'Sans date d’expiration', optional: 'Date d’expiration facultative', required: 'Date d’expiration obligatoire' };

export interface ContractorDocTypeRecord {
  id: string;
  code: string;
  label: string;
  /** exigé de chaque sous-traitant */
  required: boolean;
  expiry: DocExpiryMode;
  /** alerte « expire bientôt » N jours avant */
  warnDays: number;
  isActive: boolean;
  sortOrder: number;
}

export const contractorDocTypeInput = z.object({
  label: z.string().trim().min(2, 'Libellé obligatoire').max(100),
  required: z.boolean().default(false),
  expiry: z.enum(DOC_EXPIRY_MODES).default('optional'),
  warnDays: z.number().int().min(0).max(365).default(30),
  isActive: z.boolean().default(true),
});
export const contractorDocTypePatch = contractorDocTypeInput.partial().extend({ sortOrder: z.number().int().min(0).max(10_000).optional() });

export const DOC_REVIEW_STATUSES = ['to_review', 'approved', 'rejected'] as const;
export type DocReviewStatus = (typeof DOC_REVIEW_STATUSES)[number];
export const DOC_REVIEW_LABELS: Record<DocReviewStatus, string> = { to_review: 'À contrôler', approved: 'Contrôlé', rejected: 'Refusé' };

export interface ContractorDocumentRecord {
  id: string;
  userId: string;
  typeId: string;
  name: string;
  mime: string;
  sizeBytes: number;
  expiresOn: string | null;
  review: DocReviewStatus;
  reviewComment: string | null;
  reviewedByName: string | null;
  reviewedAt: string | null;
  uploadedByName: string;
  createdAt: string;
  /** remplacé par un document plus récent du même type (historique conservé) */
  replacedAt: string | null;
}

export const contractorDocumentMeta = z.object({
  typeId: z.string().uuid(),
  name: z.string().trim().max(150).optional(),
  expiresOn: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullish()
    .transform((v) => v ?? null),
});

export const contractorReviewInput = z.object({
  decision: z.enum(['approved', 'rejected']),
  comment: z.string().trim().max(500).nullish().transform((v) => v || null),
});

/* ------------------------------------------------------------------ conformité (§14 : À jour / Expire bientôt / Expiré / Manquant) */

export const COMPLIANCE_STATUSES = ['ok', 'expiring', 'expired', 'missing'] as const;
export type ComplianceStatus = (typeof COMPLIANCE_STATUSES)[number];
export const COMPLIANCE_LABELS: Record<ComplianceStatus, string> = { ok: 'À jour', expiring: 'Expire bientôt', expired: 'Expiré', missing: 'Manquant' };
const SEVERITY: Record<ComplianceStatus, number> = { ok: 0, expiring: 1, expired: 2, missing: 3 };

export interface TypeCompliance {
  typeId: string;
  label: string;
  required: boolean;
  status: ComplianceStatus | null; // null : type facultatif non fourni
  document: ContractorDocumentRecord | null;
  /** dernier document en attente de contrôle */
  toReview: boolean;
  daysLeft: number | null;
}
export interface Compliance {
  status: ComplianceStatus;
  types: TypeCompliance[];
  issues: number;
  toReview: number;
}

const dayDiff = (from: string, to: string) => Math.round((Date.UTC(+to.slice(0, 4), +to.slice(5, 7) - 1, +to.slice(8, 10)) - Date.UTC(+from.slice(0, 4), +from.slice(5, 7) - 1, +from.slice(8, 10))) / 86_400_000);

/**
 * État documentaire d'un sous-traitant. Un document REFUSÉ compte comme absent ; « à contrôler »
 * compte comme fourni (signalé aux responsables). Le document courant d'un type est le plus récent
 * non remplacé.
 */
export function complianceOf(types: ContractorDocTypeRecord[], docs: ContractorDocumentRecord[], today: string): Compliance {
  const out: TypeCompliance[] = [];
  for (const t of types.filter((x) => x.isActive).sort((a, b) => a.sortOrder - b.sortOrder)) {
    const current = docs
      .filter((d) => d.typeId === t.id && !d.replacedAt && d.review !== 'rejected')
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
    let status: ComplianceStatus | null;
    let daysLeft: number | null = null;
    if (!current) status = t.required ? 'missing' : null;
    else if (t.expiry !== 'none' && current.expiresOn) {
      daysLeft = dayDiff(today, current.expiresOn);
      status = daysLeft < 0 ? 'expired' : daysLeft <= t.warnDays ? 'expiring' : 'ok';
    } else if (t.expiry === 'required' && !current.expiresOn) status = 'missing';
    else status = 'ok';
    out.push({ typeId: t.id, label: t.label, required: t.required, status, document: current ?? null, toReview: current?.review === 'to_review', daysLeft });
  }
  const worst = out.reduce<ComplianceStatus>((w, x) => (x.status && SEVERITY[x.status] > SEVERITY[w] ? x.status : w), 'ok');
  return { status: worst, types: out, issues: out.filter((x) => x.status && x.status !== 'ok').length, toReview: out.filter((x) => x.toReview).length };
}

export const DEFAULT_CONTRACTOR_DOC_TYPES: { code: string; label: string; required: boolean; expiry: DocExpiryMode; warnDays: number }[] = [
  { code: 'kbis', label: 'Extrait KBIS (moins de 3 mois)', required: true, expiry: 'required', warnDays: 15 },
  { code: 'assurance_rc', label: 'Attestation d’assurance (RC pro / décennale)', required: true, expiry: 'required', warnDays: 30 },
  { code: 'attestation_fluides', label: 'Attestation de capacité — manipulation des fluides frigorigènes', required: true, expiry: 'required', warnDays: 60 },
  { code: 'urssaf', label: 'Attestation de vigilance URSSAF', required: false, expiry: 'required', warnDays: 15 },
  { code: 'piece_identite', label: 'Pièce d’identité du dirigeant', required: false, expiry: 'optional', warnDays: 30 },
];
